import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMechanicAuth } from '../../context/MechanicAuthContext';
import { getMechanicJobs, getMechanicNotifications } from '../../api/mechanicAuth.api';
import '../../styles/mechanic.css';

const STATUS_COLORS = {
  pending: '#f59e0b',
  confirmed: '#2563eb',
  dispatched: '#7c3aed',
  'in-progress': '#0891b2',
  completed: '#16a34a',
  cancelled: '#dc2626',
};

const StatusBadge = ({ status }) => (
  <span className="mechanic-status-badge" style={{ background: STATUS_COLORS[status] + '20', color: STATUS_COLORS[status] }}>
    {status?.replace('-', ' ')}
  </span>
);

const MechanicDashboard = () => {
  const { mechanic, logoutMechanic } = useMechanicAuth();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, completed: 0 });
  const [loading, setLoading] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    getMechanicJobs({ limit: 5 })
      .then((data) => {
        const list = data.requests || [];
        setJobs(list);
        setStats({
          total: data.pagination?.total || list.length,
          active: list.filter((r) => ['pending', 'confirmed', 'dispatched', 'in-progress'].includes(r.status)).length,
          completed: list.filter((r) => r.status === 'completed').length,
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    getMechanicNotifications({ limit: 1 })
      .then((data) => setUnreadNotifications(data.unreadCount || 0))
      .catch(() => {});
  }, []);

  const firstName = mechanic?.name?.split(' ')[0] || 'Mechanic';

  return (
    <div className="mechanic-page">
      <div className="mechanic-container">
        <div className="mechanic-header">
          <div>
            <h1 className="mechanic-title">Welcome, {firstName}!</h1>
            <p className="mechanic-subtitle">Mechanic Portal — RJ Road Assist</p>
          </div>
          <button className="mechanic-logout-btn" onClick={logoutMechanic}>Logout</button>
        </div>

        <div className="mechanic-stats">
          <div className="mechanic-stat-card blue">
            <div className="mechanic-stat-val">{stats.total}</div>
            <div className="mechanic-stat-label">Total Jobs</div>
          </div>
          <div className="mechanic-stat-card amber">
            <div className="mechanic-stat-val">{stats.active}</div>
            <div className="mechanic-stat-label">Active Jobs</div>
          </div>
          <div className="mechanic-stat-card green">
            <div className="mechanic-stat-val">{stats.completed}</div>
            <div className="mechanic-stat-label">Completed</div>
          </div>
          <div className="mechanic-stat-card purple">
            <div className="mechanic-stat-val">{parseFloat(mechanic?.rating || 5).toFixed(1)}</div>
            <div className="mechanic-stat-label">Rating</div>
          </div>
        </div>

        <div className="mechanic-quick-links">
          <Link to="/mechanic/jobs" className="mechanic-quick-link">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            My Jobs
          </Link>
          <Link to="/mechanic/earnings" className="mechanic-quick-link">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Earnings
          </Link>
          <Link to="/mechanic/notifications" className="mechanic-quick-link">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Notifications
            {unreadNotifications > 0 && (
              <span
                style={{
                  background: '#dc2626',
                  color: '#fff',
                  borderRadius: '9999px',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  padding: '0.0625rem 0.375rem',
                  marginLeft: '0.375rem',
                }}
              >
                {unreadNotifications}
              </span>
            )}
          </Link>
          <Link to="/mechanic/profile" className="mechanic-quick-link">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            My Profile
          </Link>
        </div>

        <h3 className="mechanic-section-title">Recent Jobs</h3>

        {loading ? (
          <div className="mechanic-loading"><div className="auth-spinner" /></div>
        ) : jobs.length === 0 ? (
          <div className="mechanic-empty">
            <p>No jobs assigned yet.</p>
          </div>
        ) : (
          <div className="mechanic-job-list">
            {jobs.map((job) => (
              <div key={job.id} className="mechanic-job-card">
                <div className="mechanic-job-info">
                  <div className="mechanic-job-tracking">{job.trackingId}</div>
                  <div className="mechanic-job-customer">{job.customerName}</div>
                  <div className="mechanic-job-service">{job.serviceName || job.service?.name || 'Service'}</div>
                  {job.locationAddress && (
                    <div className="mechanic-job-location">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {job.locationAddress}
                    </div>
                  )}
                </div>
                <StatusBadge status={job.status} />
              </div>
            ))}
          </div>
        )}

        {jobs.length > 0 && (
          <Link to="/mechanic/jobs" className="mechanic-view-all">
            View all jobs →
          </Link>
        )}
      </div>
    </div>
  );
};

export default MechanicDashboard;
